import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Heart, Check, X, Eye, Clock, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const InterestsPageNew = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('received');
  const [receivedInterests, setReceivedInterests] = useState([]);
  const [sentInterests, setSentInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    // Wait for auth to fully load
    if (authLoading) return;
    
    // Don't fetch if not authenticated (redirect handled in render)
    if (!isAuthenticated) {
      return;
    }
    fetchInterests();
    
    // Poll for new interests every 10 seconds
    const interval = setInterval(fetchInterests, 10000);
    return () => clearInterval(interval);
  }, [authLoading, isAuthenticated]);

  const fetchInterests = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [receivedRes, sentRes] = await Promise.all([
        axios.get(`${API_URL}/api/interests/received`, { headers }),
        axios.get(`${API_URL}/api/interests/sent`, { headers })
      ]);
      
      setReceivedInterests(receivedRes.data.interests || []);
      setSentInterests(sentRes.data.interests || []);
    } catch (error) {
      console.error('Error fetching interests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (interestId) => {
    try {
      setProcessing(prev => ({ ...prev, [interestId]: 'accepting' }));
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/interests/${interestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success message
      alert('Interest accepted! You can now message this person.');
      
      // Refresh interests
      await fetchInterests();
    } catch (error) {
      console.error('Error accepting interest:', error);
      alert(error.response?.data?.detail || 'Failed to accept interest');
    } finally {
      setProcessing(prev => ({ ...prev, [interestId]: null }));
    }
  };

  const handleDecline = async (interestId) => {
    if (!confirm('Are you sure you want to decline this interest?')) return;
    
    try {
      setProcessing(prev => ({ ...prev, [interestId]: 'declining' }));
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/interests/${interestId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh interests
      await fetchInterests();
    } catch (error) {
      console.error('Error declining interest:', error);
      alert(error.response?.data?.detail || 'Failed to decline interest');
    } finally {
      setProcessing(prev => ({ ...prev, [interestId]: null }));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // ========================================
  // ALL REDIRECT LOGIC IS IN RENDER (declarative)
  // ========================================

  // 1️⃣ Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // 2️⃣ Not authenticated → redirect to login (declarative)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Show loading while fetching interests
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading interests...</p>
          </div>
        </div>
      </div>
    );
  }

  const displayInterests = activeTab === 'received' ? receivedInterests : sentInterests;
  const pendingCount = receivedInterests.filter(i => i.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Interests</h1>
          <p className="text-gray-600">Manage your interests and connections</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('received')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors relative ${
                activeTab === 'received'
                  ? 'text-navy-500 border-b-2 border-mint-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>Received</span>
                {pendingCount > 0 && (
                  <span className="bg-mint-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'sent'
                  ? 'text-navy-500 border-b-2 border-mint-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sent
            </button>
          </div>
        </div>

        {/* Interests List */}
        {displayInterests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Heart className="w-16 h-16 text-coral-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No {activeTab} interests yet
            </h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'received'
                ? 'When someone sends you an interest, it will appear here.'
                : "Interests you've sent to others will appear here."}
            </p>
            <Link
              to="/search"
              className="inline-block bg-mint-500 text-white px-6 py-3 rounded-lg hover:bg-mint-600 transition-colors"
            >
              Browse Profiles
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {displayInterests.map((interest) => {
              const person = activeTab === 'received' ? interest.sender : interest.receiver;
              const isPending = interest.status === 'pending';
              const isAccepted = interest.status === 'accepted';
              
              return (
                <div
                  key={interest.id}
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Profile Image */}
                    <Link to={`/profile/${person.id}`} className="flex-shrink-0">
                      <img
                        src={person.image || `https://ui-avatars.com/api/?name=${person.firstName}+${person.lastName}&size=100&background=random`}
                        alt={`${person.firstName} ${person.lastName}`}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <Link
                            to={`/profile/${person.id}`}
                            className="text-lg font-semibold text-gray-900 hover:text-navy-500 transition-colors"
                          >
                            {person.firstName} {person.lastName}
                            {person.isVerified && (
                              <span className="ml-2 text-green-600" title="Verified">✓</span>
                            )}
                          </Link>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            {person.age && <span>{person.age} years</span>}
                            {person.occupation && (
                              <>
                                <span>•</span>
                                <span>{person.occupation}</span>
                              </>
                            )}
                          </div>
                          {person.location && (
                            <div className="text-sm text-gray-600 mt-1">
                              📍 {person.location}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusBadge(interest.status)}
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(interest.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <Link
                          to={`/profile/${person.id}`}
                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Profile
                        </Link>

                        {activeTab === 'received' && isPending && (
                          <>
                            <button
                              onClick={() => handleAccept(interest.id)}
                              disabled={processing[interest.id]}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Check className="w-4 h-4" />
                              {processing[interest.id] === 'accepting' ? 'Accepting...' : 'Accept'}
                            </button>
                            <button
                              onClick={() => handleDecline(interest.id)}
                              disabled={processing[interest.id]}
                              className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <X className="w-4 h-4" />
                              {processing[interest.id] === 'declining' ? 'Declining...' : 'Decline'}
                            </button>
                          </>
                        )}

                        {isAccepted && (
                          <Link
                            to={`/messages/${person.id}`}
                            state={{ returnUrl: '/interests', from: 'interests' }}
                            className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors"
                          >
                            <User className="w-4 h-4" />
                            Send Message
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterestsPageNew;
